/**
 * Mixin para lidar com múltiplas unidades administrativas
 *
 * Este mixin fornece métodos utilitários para trabalhar com unidades administrativas
 * incluindo cores, filtros, formatação para API e verificações de unidade primária.
 */
import store from '@/store';
import getColorByText from '@/assets/utils/colorByText';

export default {
  methods: {
    /**
     * Obtém a cor associada a uma unidade administrativa
     * @param {Object} unit - Objeto da unidade administrativa
     * @returns {String} - Cor em formato HSL
     */
    getUnitColor(unit) {
      if (!unit) return '#000000';
      return getColorByText(unit.name);
    },

    /**
     * Renderiza os badges de múltiplas unidades administrativas
     * @param {Array} units - Array de unidades administrativas
     * @param {Number|String} primaryUnitId - ID da unidade principal a ser excluída (opcional)
     * @returns {Array} - Array de unidades filtradas
     */
    getFilteredAdminUnits(units, primaryUnitId = null) {
      if (!units || !Array.isArray(units) || units.length === 0) {
        return [];
      }

      // Se um ID principal foi fornecido, filtra-o da lista
      if (primaryUnitId) {
        return units.filter(unit => unit.id !== primaryUnitId);
      }

      return units;
    },

    /**
     * Obtém o nome/abreviação da unidade administrativa
     * @param {Object} unit - Objeto da unidade administrativa
     * @returns {String} - Abreviação ou nome da unidade
     */
    getUnitLabel(unit) {
      if (!unit) return '';
      return unit.abbreviation || unit.name || '';
    },

    /**
     * Formata os IDs das unidades para envio à API
     * @param {Array} units - Array de unidades administrativas
     * @returns {Array} - Array de IDs
     */
    getUnitIds(units) {
      if (!units || !Array.isArray(units)) return [];
      return units.map(unit => unit.id);
    },

    /**
     * Prepara as unidades administrativas para envio à API, adicionando flag de primary
     * @param {Object} primaryUnit - Unidade administrativa principal
     * @param {Array} secondaryUnits - Array de unidades administrativas secundárias
     * @returns {Array} - Array formatado para a API
     */
    prepareAdminUnitsForAPI(primaryUnit, secondaryUnits) {
      if (!primaryUnit || !primaryUnit.id) {
        return [];
      }

      const result = [];

      // Adiciona a unidade principal com flag is_primary=true
      result.push({
        id: primaryUnit.id,
        is_primary: true
      });

      // Adiciona as unidades secundárias com flag is_primary=false
      if (secondaryUnits && Array.isArray(secondaryUnits) && secondaryUnits.length > 0) {
        secondaryUnits.forEach(unit => {
          if (unit.id !== primaryUnit.id) {
            result.push({
              id: unit.id,
              is_primary: false
            });
          }
        });
      }

      return result;
    },

    /**
     * Verifica se a unidade é a primária do usuário atual
     * @param {Number|Object} unit - ID da unidade ou objeto da unidade
     * @returns {Boolean} - Verdadeiro se for a unidade principal
     */
    isPrimaryUserUnit(unit) {
      const user = store.getters.user;
      if (!user || !user.administrative_unit) return false;

      const unitId = typeof unit === 'object' ? unit.id : unit;
      return user.administrative_unit.id === unitId;
    },

    /**
     * Obtém o ID da unidade administrativa selecionada ou primária
     * @returns {Number|null} - ID da unidade ou null
     */
    getSelectedOrPrimaryUnitId() {
      const selectedUnitId = store.getters.selectedAdministrativeUnit;
      if (selectedUnitId) return selectedUnitId;

      const user = store.getters.user;
      return user && user.administrative_unit ? user.administrative_unit.id : null;
    }
  }
};


// WEBPACK FOOTER //
// ./src/mixins/admin-unit-handling.js